import { Button, IconButton, useTheme } from '@material-ui/core';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import LinkIcon from '@material-ui/icons/Link';
import OpenInBrowserIcon from '@material-ui/icons/OpenInBrowser';
import { TextLinkButton } from 'framework/components/buttons/TextLinkButton';
import { CardX } from 'framework/components/cards/CardX';
import { LabelledPropertyX } from 'framework/components/labelledProperty/LabelledPropertyX';
import { AlertDialog } from 'framework/dialogs/AlertDialog';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useLazyEffect } from 'framework/hooks/useLazyEffect';
import { useTrigger } from 'framework/hooks/useTrigger';
import {
	findEazyworksMatch_handle,
	getEazyworksPatientDataById_handle,
	importEazyworksPatient_handle,
	IPatient,
	linkEazyworkKlantWithHamsPatient_handle,
} from 'gen/ApiClient';
import { useLocalization } from 'localization/useLocalization';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useDialogsContext } from 'shared/dialogs/useDialogsContext';
import { RestrictedPageX } from 'shared/layout/RestrictedPageX';
import { calcPatientDetailRoute } from '../calcPatientDetailRoute';

export const ImportEazyworkKlantPage = () => {
	const { id } = useParams<{ id: string }>();
	const [trigger, reload] = useTrigger();
	const [match, reloadMatch] = useApiEffect(findEazyworksMatch_handle, Number(id));
	const [data, reloadData] = useApiEffect(getEazyworksPatientDataById_handle, Number(id));
	const strings = useLocalization();
	const { push } = useHistory();
	const theme = useTheme();
	const { open, confirm, cancel } = useDialogsContext(reload);

	useLazyEffect(() => {
		reloadMatch();
		reloadData();
	}, [trigger]);

	if (!data || !match) {
		return <div></div>;
	}

	const onLink = (patient: IPatient) => {
		open(
			<AlertDialog
				open
				title={`${strings.linkWithExistingPatient}`}
				content={`${strings.continue}?`}
				acceptText={strings.yesCommaContinue}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => linkEazyworkKlantWithHamsPatient_handle({ eazyworksKlantId: data!.id, hamsPatientId: patient.id })}
				accept={confirm}
			/>
		);
	};

	const onAdd = () => {
		open(
			<AlertDialog
				open
				title={`${strings.createNewPatient}`}
				content={`${strings.continue}?`}
				acceptText={strings.yesCommaContinue}
				rejectText={strings.noCommaGoBack}
				reject={cancel}
				acceptF={() => importEazyworksPatient_handle({ eazyworksKlantId: data!.id })}
				accept={confirm}
			/>
		);
	};

	return (
		<RestrictedPageX pageClaim='Patients'>
			<div
				className='df-col gap-16'
				style={{
					padding: 16,
					// paddingLeft: 8,
					maxWidth: 1280,
				}}>
				<CardX>
					<div style={{ fontWeight: 'bold' }}>{strings.import}</div>
					<LabelledPropertyX label={strings.id}>{data.id}</LabelledPropertyX>
					<LabelledPropertyX label={strings.firstName}>{data.data.firstName}</LabelledPropertyX>
					<LabelledPropertyX label={strings.lastName}>{data.data.lastName}</LabelledPropertyX>
				</CardX>
				<CardX className='df-col gap-4'>
					<div
						style={{
							fontWeight: 'bold',
						}}>
						{strings.patients}
					</div>
					{match.alreadyLinked.length > 0 && (
						<div style={{ color: theme.palette.warning.main }}>{strings.followingPatientsAlreadyLinkedToThisEazyworksPatient}</div>
					)}
					{match.alreadyLinked.map(t => (
						<TextLinkButton
							key={t.id}
							startIcon={<OpenInBrowserIcon />}
							onClick={() => push(calcPatientDetailRoute(t.id))}>
							{t.identifier}
						</TextLinkButton>
					))}
					{match.alreadyLinked.length === 0 && <em>{strings.noPatientsLinkedToThisEazyworksPatient}</em>}
				</CardX>
				<CardX className='df-col gap-4'>
					<div
						style={{
							fontWeight: 'bold',
						}}>
						{strings.matchesOnSocialSecurityNumber}
					</div>
					{match.matchOnSsNr.map(t => (
						<div
							key={t.id}
							className='df-row-ac gap-4'>
							<TextLinkButton
								key={t.id}
								startIcon={<OpenInBrowserIcon />}
								onClick={() => push(calcPatientDetailRoute(t.id))}>
								{t.identifier}
							</TextLinkButton>
							<IconButton
								size='small'
								onClick={() => onLink(t)}>
								<LinkIcon />
							</IconButton>
						</div>
					))}
					{match.matchOnSsNr.length === 0 && <em>{strings.noMatchesOnSocialSecurityNumber}</em>}
				</CardX>
				<CardX className='df-col gap-4'>
					<div
						style={{
							fontWeight: 'bold',
						}}>
						{strings.matchesOnName}
					</div>
					{match.matchOnName.map(t => (
						<div
							key={t.id}
							className='df-row-ac gap-4'>
							<TextLinkButton
								key={t.id}
								startIcon={<OpenInBrowserIcon />}
								onClick={() => push(calcPatientDetailRoute(t.id))}>
								{t.identifier}
							</TextLinkButton>
							<IconButton
								size='small'
								onClick={() => onLink(t)}>
								<LinkIcon />
							</IconButton>
						</div>
					))}
					{match.matchOnName.length === 0 && <em>{strings.noMatchesFoundOnName}</em>}
				</CardX>
				<CardX className='df-col gap-4'>
					<div
						style={{
							fontWeight: 'bold',
						}}>
						{strings.createNewPatient}
					</div>
					<div>
						<Button
							startIcon={<AddCircleOutlineIcon />}
							onClick={() => onAdd()}
							variant='outlined'
							color='primary'
							size='small'>
							{strings.createNewPatientWithThisData}
						</Button>
					</div>
				</CardX>
			</div>
		</RestrictedPageX>
	);
};
